import { Component } from '@theme/component';

/** Pointer-following image zoom; touch and the existing full-screen viewer keep native behavior. */
class ProductImageZoom extends Component {
  #controller;
  #frame = 0;
  #point;
  #pointer = matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    // Gallery zoom buttons overlay the image, so listen on its existing media container.
    const surface = this.closest('.product-media-container') || this;
    surface.addEventListener('pointermove', this.#move, { signal, passive: true });
    surface.addEventListener('pointerleave', this.#reset, { signal });
    surface.addEventListener('pointerdown', this.#reset, { signal });
    surface.addEventListener('pointercancel', this.#reset, { signal });
    window.addEventListener('scroll', this.#scroll, { signal, capture: true, passive: true });
    window.addEventListener('blur', this.#reset, { signal });
    this.#pointer.addEventListener('change', this.#reset, { signal });
  }

  disconnectedCallback() {
    this.#reset();
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  /** @param {PointerEvent} event */
  #move = (event) => {
    if (!this.#pointer.matches || event.pointerType !== 'mouse' || event.buttons || this.closest('dialog')) {
      this.#reset();
      return;
    }
    this.#point = { x: event.clientX, y: event.clientY };
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const image = this.querySelector('.product-media__image');
      if (!(image instanceof HTMLImageElement) || !image.complete || !image.naturalWidth) return;
      const rect = this.getBoundingClientRect();
      const { x, y } = this.#point;
      if (!rect.width || !rect.height || x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        this.#reset();
        return;
      }
      this.style.setProperty('--pointer-zoom-x', ((x - rect.left) / rect.width * 100) + '%');
      this.style.setProperty('--pointer-zoom-y', ((y - rect.top) / rect.height * 100) + '%');
      this.setAttribute('data-zoom-active', '');
    });
  };

  /** Ignore unrelated carousels, such as the automatically advancing hero. */
  #scroll = (event) => {
    const target = event.target;
    if (target === document || (target instanceof Element && target.contains(this))) this.#reset();
  };

  #reset = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.removeAttribute('data-zoom-active');
    this.style.removeProperty('--pointer-zoom-x');
    this.style.removeProperty('--pointer-zoom-y');
  };
}

if (!customElements.get('product-image-zoom')) {
  customElements.define('product-image-zoom', ProductImageZoom);
}
